import { DependencyList, useEffect, useState } from 'react';
import { REACT_APP_MODE } from '../../../config/env';
import { useUnsavedChangesConfirm } from '../useUnsavedChangesConfirm';
import useHistory from '../useBrowserHistory';

/**
 * Flag indicating whether this has been initialized by another component
 * or not. In the dev environment, an error is logged because blocking
 * the navigation in 2 entry points cause interference with each other.
 *
 * This warning is meant for devs to know they should review their component
 * tree and avoid blocking navigation twice
 */
let initializedBy: string | null = null;

const log = (msg: string) => {
  if (REACT_APP_MODE === 'DEVELOPMENT_MODE') {
    console.error(msg);
  }
};

const checkInitializationError = (componentName: string) => {
  if (!initializedBy) {
    initializedBy = componentName;
  } else if (initializedBy !== componentName) {
    log(
      `useNavigationBlock:: ${componentName} \n ` +
        `already initialized by ${initializedBy} which could cause unexpected behaviors` +
        "Check your components tree and make sure it's only initialized once"
    );
  }
};

/**
 * This effect is used to prompt the user for confirmation before leaving
 * the page with the browser's navigation arrows or gestures, only if there
 * is any progress (primary contact or content selected).
 *
 * @warning This can only be initialized ONCE, so check your components tree
 * to avoid interference, because multiple calls to `history.block` or
 * `unblock` overwrite each other
 */
export const useNavigationBlock = (
  shouldBlock: boolean,
  dependencies: DependencyList,
  componentName: string
) => {
  const history = useHistory();
  const showUnsavedChangesConfirm = useUnsavedChangesConfirm();

  const [retry, setRetry] = useState(0);

  useEffect(() => {
    if (!shouldBlock) {
      return;
    }

    checkInitializationError(componentName);
    const currentPathname = history.location.pathname;

    /**
     * @see https://github.com/remix-run/history/blob/v5.0.0/docs/blocking-transitions.md
     */
    const unblock = history.block((tx) => {
      const { action, location, retry } = tx;
      const isCurrentPath = location.pathname === currentPathname;

      const proceed = (shouldRestore = false) => {
        unblock();
        retry();

        /**
         * If the block must be restored, the retry timestamp is changed so it
         * triggers this hook again so the history is blocked again. This is
         * for the scenario where a replace occurrs while the navigation is blocked,
         * in that case, the user is not prompted, but the unblock function
         * is executed which removes the window listener for the beforeunload;
         * therefore, by re-running this hook, the block runs again and it
         * should prompt the user when they actually try to leave the page.
         *
         * @example when a room is saved or published, the url is replaced to
         * match the proper action and/or the room uuid, if the blocking is not
         * restored, then the user could exit without a prompt and changes
         * since the last saved would be lost.
         */
        if (shouldRestore) {
          setRetry(Date.now());
        }
      };

      // we don't want to block the navigation if it's unnecessary
      if (isCurrentPath || action === 'REPLACE') {
        // if a replace action is done, the prompt is ignored, but the next time
        // the prompt must be surfaced, so we restore the block for that to work
        proceed(true);
        return;
      }

      showUnsavedChangesConfirm({
        onCancel: () => {},
        onExit: proceed,
      });
    });

    return () => {
      // unregister the blocking prompt when the component is destroyed
      initializedBy = null;
      unblock();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dependencies, retry, shouldBlock]);
};
